import { RouteReuseStrategy } from '@angular/router/';
import { ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';
import { Injectable } from '@angular/core';

/**
 * borrowed from https://www.auroria.io/angular-route-reuse-strategy/
 *
 * reuseComponent: true 가 설정된 라우트는 detach/store 하여
 * 뒤로가기 시 컴포넌트 인스턴스(데이터 + 스크롤 위치)를 그대로 복원한다.
 */
@Injectable()
export class CacheRouteReuseStrategy implements RouteReuseStrategy {
  private storedHandles = new Map<string, DetachedRouteHandle>();
  private storedScrollPositions = new Map<string, [number, number]>();

  private getRouteKey(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .filter((r) => r.routeConfig)
      .map((r) => r.routeConfig!.path)
      .join('/');
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig || future.data['reuseComponent'];
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.data['reuseComponent'] === true;
  }

  store(route: ActivatedRouteSnapshot, detachedTree: DetachedRouteHandle): void {
    const key = this.getRouteKey(route);
    if (detachedTree) {
      this.storedHandles.set(key, detachedTree);
      this.storedScrollPositions.set(key, [window.scrollX, window.scrollY]);
    } else {
      this.storedHandles.delete(key);
      this.storedScrollPositions.delete(key);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.storedHandles.has(this.getRouteKey(route));
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    const handle = this.storedHandles.get(key) || null;
    if (handle) {
      const pos = this.storedScrollPositions.get(key);
      if (pos) {
        // 이중 rAF: 렌더 후 다음 프레임에서 스크롤 복원
        requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(pos[0], pos[1])));
      }
    }
    return handle;
  }
}
